"use client";

import React from "react";
import { Activity, Footprints, HeartPulse, Sparkles, ShieldCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface IntegrationItem {
  title: string;
  badge: string;
  description: string;
  icon: React.ElementType;
}
const integrations: IntegrationItem[] = [{
  title: "VO₂ + Biomechanics",
  badge: "Capacity & Loading",
  description: "Understand both physiological capacity and aspects of movement or loading where relevant.",
  icon: Activity
}, {
  title: "VO₂ + Physiotherapy",
  badge: "Rehabilitation Pathway",
  description: "Useful for selected individuals progressing from rehabilitation towards structured physical conditioning, where clinically appropriate.",
  icon: HeartPulse
}, {
  title: "VO₂ + Gait Assessment",
  badge: "Aerobic & Mechanics",
  description: "Potentially useful for runners wanting both aerobic and movement information.",
  icon: Footprints
}, {
  title: "RMR + Nutrition Support",
  badge: "Metabolic Baseline",
  description: "Where an appropriately qualified nutrition professional is involved, RMR provides measured resting energy information that may support individual planning.",
  icon: Sparkles
}];
export default function MovementContext() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="movementcontextintegration" data-nav="light" ref={ref} className="relative py-20 lg:py-28 bg-background text-foreground transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Integrated Physiology
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
            Put the numbers into movement context
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed text-balance">
            This service complements existing PhysAnalytica expertise, bridging precise physiological testing with established clinical assessment, functional gait evaluation, and bespoke movement plans.
          </p>
        </div>

        {/* 4 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {integrations.map((item, index) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={index} className="group relative flex flex-col justify-between border-border/80 bg-card hover:border-primary/40 transition-all duration-300 hover:shadow-md">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <Icon className="size-5" aria-hidden="true" />
                      </div>
                      <Badge variant="outline" className="text-[11px] font-normal border-border/60">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </div>
              </Card>;
        })}
        </div>

        {/* Clinical Transparency Statement */}
        <div className="mt-14 max-w-2xl mx-auto">
          <div className="flex items-start sm:items-center gap-3.5 p-4 sm:p-5 rounded-lg border border-border/70 bg-muted/40 text-muted-foreground text-sm leading-relaxed">
            <ShieldCheck className="size-5 shrink-0 text-primary mt-0.5 sm:mt-0" aria-hidden="true" />
            <p className="m-0 text-xs sm:text-sm">
              <span className="font-semibold text-foreground">Clinical note:</span> We do not automatically bundle clinical services without consent or need. Each pathway is tailored strictly to clinical appropriateness.
            </p>
          </div>
        </div>
      </div>
    </section>;
}